import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import type { PaymentSessionRequest } from '../../../../types';
import memoarLogo from '../assets/memoar-logo.png';
import { LegalFooter, SupportEmail } from './legal/legal-page';

interface PaymentSignupProps {
  onCancel?: () => void;
}

const PaymentSignup: React.FC<PaymentSignupProps> = ({ onCancel }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memoirName, setMemoirName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const request: PaymentSessionRequest = {
        success_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/payment-cancelled`,
        // Optional: the webhook names an unnamed one, and it can be renamed.
        ...(memoirName.trim() ? { memoir_name: memoirName.trim() } : {}),
      };

      const response = await apiClient.createPaymentSession(request);

      if (response.success && response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        setError(response.message || 'Failed to create payment session');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="payment-signup">
      {/* Header with Logo */}
      <header className="payment-signup__header">
        <div className="payment-signup__logo">
          <img src={memoarLogo} alt="Memoar" className="payment-signup__logo-img" />
          <span className="payment-signup__logo-text">memoar</span>
        </div>
      </header>

      <div className="payment-signup__content">
        {/* Left: Emotional Hero */}
        <section className="payment-signup__hero">
          <div className="payment-signup__hero-content">
            <h1 className="payment-signup__headline">
              Your memories deserve
              <span className="payment-signup__headline-accent">a forever home</span>
            </h1>
            <p className="payment-signup__tagline">
              Every photograph holds a story. Every story deserves to be preserved.
              Memoar keeps your precious moments safe, private, and always within reach.
            </p>
          </div>

          {/* Decorative elements */}
          <div className="payment-signup__decoration">
            <div className="payment-signup__circle payment-signup__circle--1"></div>
            <div className="payment-signup__circle payment-signup__circle--2"></div>
            <div className="payment-signup__circle payment-signup__circle--3"></div>
          </div>
        </section>

        {/* Right: Plan Card & Checkout */}
        <section className="payment-signup__checkout">
          <div className="payment-signup__card">
            {/* Plan Badge */}
            {/* SOL-204: what is sold is one Memoir, once -- not a yearly plan.
                "Memoar" is only ever the wordmark; "Memoir" only ever a
                countable noun with an article. Claims here stay to what
                exists today: no client free year (SOL-185 is not built), no
                privacy promise beyond the true one (SOL-203). */}
            <div className="payment-signup__plan-badge">
              <span className="payment-signup__plan-label">Your first Memoir</span>
            </div>

            <h2 className="payment-signup__plan-name">A Memoir</h2>
            <p className="payment-signup__plan-meaning">
              <span>a memoir in photographs: one complete delivery to one client</span>
            </p>

            {/* Price */}
            <div className="payment-signup__price">
              <span className="payment-signup__price-amount">30,00</span>
              <span className="payment-signup__price-currency">EUR + VAT</span>
              <span className="payment-signup__price-period">paid once</span>
            </div>

            {/* Benefits */}
            <ul className="payment-signup__benefits">
              <li>
                <span className="payment-signup__benefit-icon"></span>
                <span>Upload the whole delivery, organised into albums</span>
              </li>
              <li>
                <span className="payment-signup__benefit-icon"></span>
                <span>Share it by private link, or hand it over with a code</span>
              </li>
              <li>
                <span className="payment-signup__benefit-icon"></span>
                <span>Download everything as a ZIP, any time</span>
              </li>
              <li>
                <span className="payment-signup__benefit-icon"></span>
                <span>Buy another Memoir for each new client</span>
              </li>
            </ul>

            {/* Error display */}
            {error && (
              <div className="payment-signup__error">
                <p>{error}</p>
                <p>If it keeps happening, write to <SupportEmail />.</p>
              </div>
            )}

            {/* CTA */}
            <form onSubmit={handleSubmit} className="payment-signup__form">
              <label className="payment-signup__name-label" htmlFor="first-memoir-name">
                Name it <span>(optional)</span>
              </label>
              <input
                id="first-memoir-name"
                className="payment-signup__name-input"
                type="text"
                maxLength={100}
                value={memoirName}
                onChange={(e) => setMemoirName(e.target.value)}
                placeholder="e.g. Giulia & Marco, 14 June"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="payment-signup__cta"
              >
                {isLoading ? (
                  <span className="payment-signup__cta-loading">
                    <span className="payment-signup__spinner"></span>
                    Preparing...
                  </span>
                ) : (
                  'Continue to payment'
                )}
              </button>

              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="payment-signup__cancel"
                  disabled={isLoading}
                >
                  Go Back
                </button>
              )}
            </form>

            {/* Security reassurance */}
            <div className="payment-signup__security">
              <div className="payment-signup__security-icon"></div>
              <p>
                Secure checkout powered by Stripe. Prices exclude VAT; a business
                can enter its VAT ID at checkout. You'll be asked to accept our
                Terms of Service there. After payment, you'll receive a six-digit
                code to open your account.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="payment-signup__footer">
        <p>Memories preserved with care</p>
        <p className="payment-signup__login-link">
          Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Sign in</a>
        </p>
        <LegalFooter className="payment-signup__legal" />
      </footer>
    </div>
  );
};

export default PaymentSignup;
